import { React, useEffect, useState } from 'react';
import { selectFerpaState } from '../../../../../../features/authentication/authSlice';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import ReportOptions from '../../components/reportOptions';
import TableSkeleton from '../../../../../../components/SkeletonLoader/TableSkeleton';
import { DateFormatter } from '../../../../../../utils/DateFormatter';
import { colorCode } from '../../../../utils/constants';
import { useGetAllStudentAssignmentsScoreMutation } from '../../../../../../features/authentication/reportApiSlice';
import CustomToast from '../../../../../../components/Toast';
import {ScoreCalculator} from '../../../../utils/ScoreCalculator';
import { useReportContext } from '../../../../components/reportContext';
import ReportHeader from '../../components/reportHeader';
import FERPA from '../../../../../../components/FERPA/FerpaComponent';
import { convertToCSV, downloadCSV, reorderObjectProperties } from '../../../../../assignments/utils';
import {
  useGetTeacherReportOptionsMutation
} from '../../../../../../features/authentication/persistApiSlice';
import PageSize from '../../../../../../enums/pageSize';
import usePrintPdfGenerator from '../../../../../../utils/PrintPdfGenerator';
import ProgressButton from '../../../../../../components/ProgressButton';
import './styles/allStudentsScoreInfo.css';

const AllStudentsScoreInfo = () => {
  const navigate = useNavigate();
  const ferpaEnabled = useSelector(selectFerpaState);
  const { selectedCourseId, startDate, endDate, courseName, studentToIncludeTab, className, selectedClassIds, isAssessment } = useReportContext();
  const titleMaxLength = 50;
  const reportTitle = `${isAssessment ? 'Assessment' : 'Assignment'} Scores for ${studentToIncludeTab == 0 ? 'All Students' : studentToIncludeTab == 1 && selectedClassIds == -1 ? 'All Classes' : className}`;
  const shouldTruncateReportTitle = reportTitle.length > titleMaxLength;
  const truncatedReportTitle = shouldTruncateReportTitle ? reportTitle.slice(0, titleMaxLength) : reportTitle;

  const [reportDate, setReportDate] = useState('');
  const [expand, setExpand] = useState(false);
  const [isLoadingReportData, setIsLoadingReportData] = useState(true);
  const [assignmentDetails, setAssignmentDetails] = useState([]);
  const [studentDetails, setStudentDetails] = useState([]);
  const [unansweredAsIncorrect, setUnansweredAsIncorrect] = useState(null);
  const [retryScore, setRetryScore] = useState(null);
  const [displayName, setDisplayName] = useState(null);
  const [displayId, setDisplayId] = useState(null);
  const [isCsvGenerating, setIsCsvGenerating] = useState(false);
  const [assignmentScoreDetails, setAssignmentScoreDetails] = useState([]);
  const [dateTimeBasedUniqueKey, setDateTimeBasedUniqueKey] = useState(
    new Date().getTime().toString()
  );
  const { printPDF, isPdfGenerating } = usePrintPdfGenerator();

  const [getAllStudentAssignmentsScoreMutation] = useGetAllStudentAssignmentsScoreMutation();
  const [getTeacherReportOptions] = useGetTeacherReportOptionsMutation();
  const scoreTableClassNames = `reportTable all-students-score-table ${displayName ? 'show-name' : ''} ${displayId ? 'show-id' : ''}`;

  useEffect(() => {
    checkContextData();
    getReportOptions();
  }, []);

  const checkContextData = () => {
    if(selectedCourseId == -1)
      navigate(-1);
  };

  const getReportOptions = async () => {
    let response = await getTeacherReportOptions().unwrap();
    setUnansweredAsIncorrect(response.incompleteScoring);
    setRetryScore(response.retryScore);
    setDisplayName(response.displayName);
    setDisplayId(response.displayId);

    await handleReportData(response.retryScore, response.incompleteScoring);
    setIsLoadingReportData(false);
  };

  const handleReportData = async (retryScore, incompleteScoring) => {
    if(selectedCourseId == -1)
      return;
    try{
      const options={
        classIds: studentToIncludeTab == 1 ? selectedClassIds : [-2],
        startDate: startDate,
        endDate: endDate,
        courseId: selectedCourseId,
        isAssessment: isAssessment
      };

      let response = await getAllStudentAssignmentsScoreMutation(options);
      if (response.error) {
        CustomToast(response.error.data.error, 'error');
      }
      else {
        setReportDate(response.data.reportDate);
        setAssignmentDetails(response.data.assignmentDetails);
        setStudentDetails(response.data.studentDetails);
        updateAssignmentDetails(response.data.assignmentDetails, response.data.studentDetails, retryScore, incompleteScoring);
      }
    } catch (error) {
      console.log(error);
    }
    setDateTimeBasedUniqueKey(new Date().getTime().toString());
  };

  const updateAssignmentDetails = (assignments, studentScores, retryScore, incompleteScoring) => {
    assignments = assignments ?? assignmentDetails;
    studentScores = studentScores ?? studentDetails;
    let studentAssignmentDetails=[];
    
    studentScores.map(student => {
      let scoreDetails = [];

      assignments.forEach(assignment => {
        const { assignmentId } = assignment;
        const scoreDetail = student.scoreDetails.find(detail => detail.assignmentId === assignmentId);
        
        if (scoreDetail) {
          let score = ScoreCalculator(retryScore, incompleteScoring ? 1 : 0, scoreDetail.assignmentScoreCount, scoreDetail.correct, scoreDetail.incorrect, scoreDetail.correctOnRetry, scoreDetail.crPoints, scoreDetail.crAnswered, scoreDetail.crNotAnswered, scoreDetail.crMaxPoints, scoreDetail.statusNewCount);
          scoreDetails.push(score>=0 && score<=100 ? `${score}%` : score);
        } else {
          scoreDetails.push('');
        }
      });

      let assignmentDetail = {
        studentName: student.name,
        studentId: student.id,
        scores: scoreDetails 
      };
      studentAssignmentDetails.push(assignmentDetail);
    });
    setAssignmentScoreDetails(studentAssignmentDetails);
  };

  const exportHandler = () => {
    const timePeriod = `${DateFormatter.toShortDate2(startDate)} to ${DateFormatter.toShortDate2(endDate)}`;
    const reportDetails = {
      Report : reportTitle,
      Course : courseName,
      'Time Period': timePeriod,
      'Report Date': DateFormatter.toLongDateTime(reportDate, true)
    };

    const studentReportData = [];
    const assignmentReportData = [];
    const scoreDescription = [];
    const assignmentColumnHeaders = assignmentDetails.map((assignment, index) => {
      return assignment?.description.trim() || `Assignment ${index + 1}`;
    });

    assignmentScoreDetails.forEach((student) => {
      const studentData = {};

      if (displayName) {
        studentData['Student Name'] = ferpaEnabled ? student.studentName : 'name hidden';
      }

      if (displayId) {
        studentData['Student ID'] = ferpaEnabled ? student.studentId : 'id hidden';
      }

      for (let i = 0; i < assignmentDetails.length; i++) {
        const assignmentKey = `##_ASSIGNMENT_${i}_##${assignmentColumnHeaders[i]}`;
        studentData[assignmentKey] = student.scores?.[i] || '';
      }

      studentReportData.push(studentData);
    });

    assignmentDetails.forEach((report, index) => {
      assignmentReportData.push({
        'No': `A${index + 1}`,
        'Assignment Name': report.description,
        'Last Assigned': DateFormatter.toShortDate2(report.assignDate)
      });      
    });

    scoreDescription.push({
      'Not Scored': 'NS',
      'Not Started': 'N',
      'In Progress': 'P'
    });
    
    const newData = {
      ReportOptions : reportDetails,
      ',' : studentReportData,
      'Assignments' : assignmentReportData,
      '' : scoreDescription
    };

    const newDataWithReportOptionsFirst = reorderObjectProperties(newData, 'ReportOptions');
    const csvData = convertToCSV(newDataWithReportOptionsFirst);
    downloadCSV(csvData, 'All Assignment All Students Score' );
    setIsCsvGenerating(false);
  };

  const splitStudentsTableForPrint = (
    container,
    assignmentColumnsPerTable = 8
  ) => {
    const studentsListWrapper = container.querySelector(
      '#studentsList .assignments-scroll-bar'
    );
    const sourceTable = studentsListWrapper?.querySelector('table');
    const headerRow = sourceTable?.querySelector('thead tr');
    const bodyRows = sourceTable?.querySelectorAll('tbody tr');

    if (!studentsListWrapper || !sourceTable || !headerRow || !bodyRows?.length) {
      return;
    }

    const allHeaderCells = [...headerRow.children];
    const stickyColumnsCount =
    (displayName ? 1 : 0) + (displayId ? 1 : 0);
    const assignmentHeaderCells = allHeaderCells.slice(stickyColumnsCount);

    if (assignmentHeaderCells.length <= assignmentColumnsPerTable) {
      return;
    }

    const sourceHead = sourceTable.querySelector('thead');
    const sourceBody = sourceTable.querySelector('tbody');

    const cloneCells = (cells, start, end) =>
      cells.slice(start, end).map((cell) => cell.cloneNode(true));

    const setStyles = (element, styles, important = false) => {
      Object.entries(styles).forEach(([property, value]) => {
        element.style.setProperty(
          property,
          value,
          important ? 'important' : ''
        );
      });
    };

    const splitTablesContainer = document.createElement('div');
    splitTablesContainer.className = 'd-flex flex-column';
    splitTablesContainer.style.gap = '10px';

    for (
      let start = 0;
      start < assignmentHeaderCells.length;
      start += assignmentColumnsPerTable
    ) {
      const end = start + assignmentColumnsPerTable;

      const splitTable = sourceTable.cloneNode(false);
      const splitHead = sourceHead.cloneNode(false);
      const splitHeaderRow = headerRow.cloneNode(false);

      splitHeaderRow.append(
        ...cloneCells(allHeaderCells, 0, stickyColumnsCount),
        ...cloneCells(assignmentHeaderCells, start, end)
      );

      splitHead.appendChild(splitHeaderRow);
      splitTable.appendChild(splitHead);

      const splitBody = sourceBody.cloneNode(false);

      bodyRows.forEach((row) => {
        const rowCells = [...row.children];
        const splitRow = row.cloneNode(false);

        splitRow.append(
          ...cloneCells(rowCells, 0, stickyColumnsCount),
          ...cloneCells(
            rowCells,
            stickyColumnsCount + start,
            stickyColumnsCount + end
          )
        );

        splitBody.appendChild(splitRow);
      });

      splitTable.appendChild(splitBody);

      splitTable
        .querySelectorAll(
          'th, .sticky-student-name-column, .sticky-student-id-column'
        )
        .forEach((cell) => {
          setStyles(
            cell,
            {
              position: 'static',
              top: 'auto',
              left: 'auto',
              zIndex: 'auto'
            },
            true
          );
        });

      setStyles(splitTable, {
        marginBottom: '18px',
        pageBreakInside: 'avoid',
        breakInside: 'avoid',
        width: '100%'
      });

      splitTablesContainer.appendChild(splitTable);
    }

    studentsListWrapper.classList.remove('max-h-550');

    setStyles(
      studentsListWrapper,
      {
        maxHeight: 'none',
        height: 'auto',
        overflow: 'visible',
        overflowX: 'visible',
        overflowY: 'visible',
        position: 'static'
      },
      true
    );

    const studentsListContainer = container.querySelector('#studentsList');

    if (studentsListContainer) {
      setStyles(
        studentsListContainer,
        {
          marginBottom: '16px',
          pageBreakInside: 'avoid',
          breakInside: 'avoid'
        },
        true
      );
    }

    const assignmentListContainer = container.querySelector('#assignmentList');

    if (assignmentListContainer) {
      setStyles(
        assignmentListContainer,
        {
          marginTop: '12px',
          position: 'relative',
          zIndex: '1'
        },
        true
      );
    }

    studentsListWrapper.replaceChildren(splitTablesContainer);
  };

  const printHandler = async () => {
    if (isPdfGenerating) return;

    const contentWrapper = document.getElementById('reportWrapper');
    const htmlString = contentWrapper.innerHTML;

    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = htmlString;

    const ferpaContainer = tempContainer.querySelector('#ferpaContainer');
    if (ferpaContainer) {
      ferpaContainer.classList.add('d-none');
    }

    const buttonElements = tempContainer.querySelectorAll('button');
    buttonElements.forEach((svgElement) => {
      svgElement.classList.add('d-none');
    });

    const reportOptionBtn = tempContainer.querySelector('#reportOptionsDiv');
    if(reportOptionBtn) {
      reportOptionBtn.classList.add('d-none');
    }

    const reportOptions = tempContainer.querySelector('#reportOptions');
    if(reportOptions) {
      reportOptions.classList.remove('d-none');
    }

    splitStudentsTableForPrint(tempContainer);

    const reportTitleElement = tempContainer.querySelector('#reportTitle');
    if(reportTitleElement) {
      reportTitleElement.textContent = reportTitle;
    }

    await printPDF('All_Assignment_Single_Student_Score', tempContainer.innerHTML, PageSize.A4);
  };

  return (
    <div className="white-card">
      <div id="reportWrapper">
        <div className="d-flex align-items-center justify-content-between pb-4">
          <h2 id="reportTitle" className="primary-title mb-md-0">
            {truncatedReportTitle}
            {shouldTruncateReportTitle && (
              <OverlayTrigger
                placement="right"
                overlay={<Tooltip className="custom-tooltip">{reportTitle}</Tooltip>}>
                <span className="cursor-pointer">...</span>
              </OverlayTrigger>
            )}
          </h2>
          <div id="ferpaContainer" className="d-flex justify-content-end align-items-center gap-3">
            <FERPA />
            <button id="back_button" onClick={() => navigate(-1)} className="btn btn-width btn-castleWhite">
              Back
            </button>
            <button id="export_button" onClick={async() => {setIsCsvGenerating(true); exportHandler();}} className="btn btn-width btn-castleOrange p-0" disabled={isCsvGenerating || isLoadingReportData}>
              {isCsvGenerating == true ? 'Generating CSV' : 'Export'}
            </button>
            <ProgressButton
              isLoading={isPdfGenerating}
              onClick={printHandler}
              loadingLabel="Generating PDF"
              label="Print to PDF"
              className="btn btn-castleRed p-0"
              disabled={isLoadingReportData}
            />
          </div>
        </div>
        <ReportHeader
          isLoading={isLoadingReportData}
          startDate={startDate}
          endDate={endDate}
          reportDate={reportDate}
          courseName={courseName}
        />
        <div className='align-items-baseline d-flex justify-content-between pt-3'>
          <div id="reportOptionsDiv" className='d-flex fw-medium text-castleOrange cursor-pointer report-button'
            onClick={() => setExpand(!expand)}>
            Report Options
            <div className='align-items-center cursor-pointer d-flex'>
              <FontAwesomeIcon className='fs-7 ms-2' icon={expand ? faMinus : faPlus} />
            </div>
          </div>
          <div id="scoringDescription" className='d-flex pb-4'>
            {colorCode.map((item) => (
              <div key={item.name} className='align-items-center d-flex px-4'>
                <div className='d-flex rounded-circle justify-content-center' style={{ backgroundColor: item.backgroundColor }}>
                  {item.icon}
                </div>
                <span className='px-2'>{item.name}</span>
              </div>
            ))}
          </div>
        </div>
        <ReportOptions
          key={dateTimeBasedUniqueKey}
          expand={expand}
          isSingleStudent={false}
          retryScore={retryScore}
          setRetryScore={setRetryScore}
          unansweredAsIncorrect={unansweredAsIncorrect}
          setUnansweredAsIncorrect={setUnansweredAsIncorrect}
          displayName={displayName}
          setDisplayName={setDisplayName}
          displayId={displayId}
          setDisplayId={setDisplayId}
          updateAssignmentDetails={updateAssignmentDetails}
        />
        <div className='pt-4'>
          <div id='studentsList' className='pb-4'>
            <div className='assignments-scroll-bar max-h-550 custom-table table-responsive p-0'>
              <table className={`table ${scoreTableClassNames}`}>
                {
                  studentDetails?.length > 0 && !isLoadingReportData ? (
                    <>
                      <thead className='thead-light align-middle'>
                        <tr>
                          {displayName &&
                            <th id='studentName' scope="col" className='fw-medium sticky-student-name-column'>
                              Student Name
                            </th>
                          }
                          {displayId &&
                            <th id='studentId' scope="col" className='fw-medium sticky-student-id-column'>
                              Student ID
                            </th>
                          }
                          {[...Array(assignmentDetails.length)].map((_, index) => (
                            <th id={'assignmentNo' + index} key={'assignment'+ (index+1)} scope="col" className='fw-medium text-center p-0'>
                              {assignmentDetails.length <= 10 ? (
                                <span className='d-block px-2'>
                                  {assignmentDetails[index]?.description}
                                </span>
                              ) : (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip className='custom-tooltip' id={`assignment-tooltip-${index}`}>
                                      {assignmentDetails[index]?.description}
                                    </Tooltip>
                                  }
                                >
                                  <span className='cursor-pointer'>A{index+1}</span>
                                </OverlayTrigger>
                              )}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {assignmentScoreDetails?.map((item) => (
                          <tr key={item.Id}>
                            {displayName &&
                              <td className='sticky-student-name-column text-wrap'>{ferpaEnabled ? item.studentName : 'name hidden'}</td>
                            }
                            {displayId &&
                              <td className='sticky-student-id-column'>{ferpaEnabled ? item.studentId : 'id hidden'}</td>
                            }
                            {item.scores.map((score, index) => {
                              let colorItem = colorCode.find(item => item.label == score);
                              return (
                                <td key={'score' + index} className='text-center'>
                                  {colorItem ?
                                    <span className='d-flex justify-content-center'>
                                      <div className='rounded-circle circleBtn' style={{ backgroundColor: colorItem.backgroundColor }}>
                                        {colorItem.icon}
                                      </div>
                                    </span>
                                    : score}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </>
                  ) : isLoadingReportData && (
                    <>
                      <TableSkeleton
                        colCount={8}
                        rowCount={6}
                      />
                    </>
                  )
                }
              </table>
            </div>
          </div>

          <div id='assignmentList' className='pt-3'>
            <h2 className='fw-medium pb-3'>Assignments</h2>
            <div className='assignments-scroll-bar max-h-550 custom-table table-responsive p-0'>
              <table className='table reportTable'>
                {
                  assignmentDetails.length > 0 && !isLoadingReportData ? (
                    <>
                      <thead className='thead-light align-middle'>
                        <tr>
                          <th id='no' scope='col' className='fw-medium'>
                            No.
                          </th>
                          <th id='description' scope="col" className='fw-medium min-w-400 text-start'>
                            Assignment Name
                          </th>
                          <th id='assignDate' scope="col" className='fw-medium'>
                            Last Assigned
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignmentDetails?.map((item, index) => (
                          <tr key={item.assignmentId}>
                            <td className='text-center'>A{index+1}</td>
                            <td className='w-min-max-400 text-truncate text-center'>{item.description}</td>
                            <td className='text-center'>{DateFormatter.toShortDate2(item.assignDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </>
                  ) : isLoadingReportData ? (
                    <>
                      <TableSkeleton
                        colCount={3}
                        rowCount={6}
                      />
                    </>
                  ) :
                    <p className='text-danger'>There are no results for the selected report options.</p>
                }
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllStudentsScoreInfo;
